import { useCallback, useMemo } from 'react';
import { Select } from '@components/base/inputs';

export interface SessionOption {
  id: number;
  label: string;
}

export interface SessionSwitcherProps {
  /** Available sessions to choose from */
  sessions: SessionOption[];
  /** Currently active session ID */
  activeSessionId: number | null;
  /** Called when the user selects a different session */
  onSessionChange: (sessionId: number, label: string) => void;
  /**
   * Show a search box in the dropdown to filter the sessions (passed to the
   * base `Select`). Useful when a user can see thousands of sessions
   * (extranet SUPERVUE users). Defaults to `false` — the original,
   * non-searchable dropdown.
   */
  searchable?: boolean;
}

export function SessionSwitcher({
  sessions,
  activeSessionId,
  onSessionChange,
  searchable = false,
}: Readonly<SessionSwitcherProps>) {
  const options = useMemo(
    () => sessions.map((s) => ({ value: String(s.id), label: s.label })),
    [sessions],
  );

  const handleChange = useCallback(
    (value: string | string[]) => {
      const selectedId = Array.isArray(value) ? value[0] : value;
      if (!selectedId) return;
      const session = sessions.find((s) => String(s.id) === selectedId);
      if (session) {
        onSessionChange(session.id, session.label);
      }
    },
    [sessions, onSessionChange],
  );

  if (sessions.length < 2) return null;

  return (
    <Select
      options={options}
      value={activeSessionId != null ? String(activeSessionId) : ''}
      onChange={handleChange}
      size="sm"
      searchable={searchable}
    />
  );
}
